import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  AlertCircle,
  TrendingUp,
  GraduationCap,
  ChevronRight,
  XCircle,
  Info
} from 'lucide-react';

const AttendanceTips = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const tips = [
    {
      id: 'exams',
      title: 'End-Semester Exams',
      description: 'Minimum 75% overall attendance is required to be eligible for end-semester exams.',
      detailedInfo: 'Students must maintain at least 75% attendance in all subjects to appear for end-semester examinations. This rule applies uniformly to all students regardless of their academic performance.',
      icon: GraduationCap,
      color: 'teal',
      requirement: '75%+'
    },
    {
      id: 'medical',
      title: 'Medical Condonation (65-74%)',
      description: 'Eligible for medical condonation (allowed only once in the entire course).',
      detailedInfo: 'If attendance is between 65% and 74%, students may apply for medical condonation. This requires valid medical certificates and is permitted only once during the entire course duration.',
      icon: AlertCircle,
      color: 'orange',
      requirement: '65-74%'
    },
    {
      id: 'move',
      title: 'Move to Next Semester (50-64%)',
      description: 'Allowed only once. CA marks are carried forward; Final Exams marked absent.',
      detailedInfo: 'Students with 50-64% attendance can move to the next semester but must appear for supplementary exams. This provision can be availed only once during the academic program.',
      icon: TrendingUp,
      color: 'yellow',
      requirement: '50-64%'
    },
    {
      id: 'stoppage',
      title: 'Stoppage (<50%)',
      description: 'All CA and FE cancelled. Must rejoin the same semester when next offered.',
      detailedInfo: 'Attendance below 50% results in complete stoppage. All Continuous Assessment and Final Examination marks are cancelled. Students must rejoin the semester when it is offered again.',
      icon: XCircle,
      color: 'red',
      requirement: '<50%'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      teal: {
        bg: 'bg-[#0d4f5c]/10',
        border: 'border-[#0d4f5c]/20',
        text: 'text-[#0d4f5c]',
        icon: 'text-[#0d4f5c]',
        hover: 'hover:bg-[#0d4f5c]/20'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        icon: 'text-orange-500',
        hover: 'hover:bg-orange-100'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        icon: 'text-yellow-500',
        hover: 'hover:bg-yellow-100'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        icon: 'text-red-500',
        hover: 'hover:bg-red-100'
      }
    };
    return colors[color] || colors.teal;
  };

  const getStatusBadge = (requirement) => {
    if (requirement.includes('75')) {
      return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">✅ Eligible</span>;
    } else if (requirement.includes('65')) {
      return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">⚠️ Conditional</span>;
    } else if (requirement.includes('50')) {
      return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">⚠️ At Risk</span>;
    } else {
      return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">❌ Ineligible</span>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      className="bg-gradient-to-br from-[#0d4f5c]/5 via-white to-[#1a7a8a]/5 rounded-2xl border border-[#0d4f5c]/20 p-6 shadow-lg"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] rounded-lg shadow-lg shadow-[#0d4f5c]/20">
          <Award className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">Attendance Guidelines</h3>
          <p className="text-sm text-gray-500">Important rules and policies for attendance management</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tips.map((tip, i) => {
          const colors = getColorClasses(tip.color);
          const Icon = tip.icon;
          const isExpanded = expandedSection === tip.id;

          return (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className={`${colors.bg} ${colors.hover} border ${colors.border} rounded-xl p-4 transition-all duration-300 cursor-pointer shadow-sm`}
              onClick={() => setExpandedSection(isExpanded ? null : tip.id)}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 ${colors.bg} rounded-lg`}>
                  <Icon className={`h-5 w-5 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-semibold ${colors.text}`}>{tip.title}</h4>
                      {getStatusBadge(tip.requirement)}
                    </div>
                    <ChevronRight className={`h-4 w-4 ${colors.text} transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                  <p className={`text-sm ${isExpanded ? 'block' : 'line-clamp-2'} text-gray-600 mt-1`}>
                    {tip.description}
                  </p>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-3 pt-3 border-t border-gray-200"
                    >
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {tip.detailedInfo}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <Info className="h-3 w-3 text-gray-400" />
                        <p className="text-xs text-gray-500">
                          Click again to collapse • Reference: Academic Policy Manual
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-4 p-4 bg-gradient-to-r from-[#0d4f5c]/20 to-[#1a7a8a]/20 rounded-lg border border-[#0d4f5c]/20">
        <div className="flex items-start gap-3">
          <div className="p-1 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] rounded-full mt-0.5">
            <Info className="h-3 w-3 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#0d4f5c] font-medium">Important Note</p>
            <p className="text-sm text-[#0d4f5c]">
              Students under <strong>"Stoppage"</strong> category will have no classification of class for that semester.
              All academic activities for that semester will be cancelled.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AttendanceTips;